<template>
  <div class="display-in-center view relative" v-loading.fullscreen="loading">
    <div v-if="barcode">
      <div class="current-tracking-point-container">{{ currentTrackingName }}</div>
      <FormBuilder
        :isShownFloatingActionButton="isFABButtonAvailable"
        :passedButtons="allowSkipTracking ? buttonsArrayInTrack : []"
        :showBackButton="true"
        v-if="selectedTarget"
        :key="formBuilderKey"
        :formSchema="selectedFormSchema"
        :formModel="formModel"
        @submit="confirmSubmitDialog"
        @reviewCalculation="reviewCalculation"
        @back="onBackRouter"
      />
      <div v-if="hasRemarks" style="margin-bottom: 20px">
        <div class="text-left my-4">{{ $t('remarks') }}</div>
        <div class="remarks-content" v-html="mergedHtml" />
      </div>
    </div>
    <el-dialog
      :lock-scroll="true"
      width="90%"
      top="0"
      :model-value="showDialogAlert"
      :close-on-click-modal="false"
      :show-close="false"
      @closed="showDialogAlert = false"
    >
      <div class="dialog-message mb-3">{{ $t(dialogMessage) }}</div>
      <PreviewTracking
        v-if="isAddTrackingDialog && previewAddTrackingVisiblity"
        :barcode="barcode"
        :project="project"
        :trackingData="previewTrackingData"
        :fileListFormModel="fileListFormModel"
      />
      <template #footer>
        <span class="dialog-footer">
          <div>
            <el-button
              type="primary"
              @click="() => (isAddTrackingDialog ? submit(false) : submit(true))"
              class="full-w-button el-button--primary is-small"
            >
              {{ $t('OK') }}
              <KeyenceHTHardKeyButton
                v-if="isKeyenceEnterpriseBrowser"
                :hardkey="KeyenceHardKey.ENTER"
                :function="() => (isAddTrackingDialog ? submit(false) : submit(true))"
                :visible="showDialogAlert"
                :newLayer="true"
              />
            </el-button>
          </div>
          <!-- <div v-if="isAllowBulkUpdateAtCurrentTrackPoint || !isAddTrackingDialog">
            <el-button
              v-if="isKeyenceEnterpriseBrowser"
              type="primary"
              @click="
                onRouteTo(
                  isAddTrackingDialog
                    ? 'bulk-tracking-with-keyence-handyterminal'
                    : 'bulk-skip-tracking-with-keyence-handyterminal'
                )
              "
              class="full-w-button el-button--primary is-small"
            >
              {{
                $t(
                  isAddTrackingDialog
                    ? 'ok_scan_barcode_to_track_multiple_barcodes'
                    : 'ok_scan_barcode_to_skip_multiple_barcodes_tracking'
                )
              }}
            </el-button>
            <el-button
              v-else
              @click="onRouteTo(isAddTrackingDialog ? 'bulk-tracking' : 'bulk-skip-tracking')"
              class="full-w-button el-button--primary is-small "
            >
              {{
                $t(
                  isAddTrackingDialog
                    ? 'ok_scan_barcode_to_track_multiple_barcodes'
                    : 'ok_scan_barcode_to_skip_multiple_barcodes_tracking'
                )
              }}
            </el-button>
          </div> -->
          <div>
            <el-button @click="showDialogAlert = false" class="full-w-button el-button--primary is-small clear-button">
              {{ $t('cancel') }}
              <KeyenceHTHardKeyButton
                v-if="isKeyenceEnterpriseBrowser"
                :hardkey="KeyenceHardKey.BACKSPACE"
                :function="() => (showDialogAlert = false)"
                :visible="showDialogAlert"
                :newLayer="false"
              />
            </el-button>
          </div>
        </span>
      </template>
    </el-dialog>
    <SkippingCustomValidationDialog
      :visible="showSkipValidationDialog"
      :message="skipValidationMessage"
      title=""
      @proceed="proceedWithSkippedValidation"
      @close="closeDuplicateCodesDialog"
    />
    <ReviewCalculationDialog v-model:isShow="isShowCalculationDialog" :content="calculationContent" />

    <el-dialog
      :lock-scroll="true"
      width="90%"
      top="0"
      :model-value="showExistingDataErrorDialog"
      :close-on-click-modal="false"
      :show-close="false"
      @closed="showExistingDataErrorDialog = false"
    >
      <div class="dialog-message mb-3">{{ $t('errors.3065') }}</div>
      <template #footer>
        <span class="dialog-footer">
          <div>
            <el-button
              type="primary"
              @click="confirmMergeCurrentSerialWithNewestData"
              class="full-w-button el-button--primary is-small"
            >
              {{ $t('ok') }}
              <KeyenceHTHardKeyButton
                v-if="isKeyenceEnterpriseBrowser"
                :hardkey="KeyenceHardKey.ENTER"
                :function="confirmMergeCurrentSerialWithNewestData"
                :visible="showExistingDataErrorDialog"
                :newLayer="true"
              />
            </el-button>
          </div>
        </span>
      </template>
    </el-dialog>
  </div>
</template>
<script lang="ts">
import ReviewCalculationDialog from '@/components/dialog/ReviewCalculationDialog.vue'
import FormBuilder, { IFormButton } from '@/components/FormBuilder.vue'
import KeyenceHTHardKeyButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeyButton.vue'
import BarcodeTypesMixin from '@/components/mixins/BarcodeTypesMixin.vue'
import CustomFieldsMixin from '@/components/mixins/CustomFieldsMixin.vue'
import PreviewTracking from '@/components/PreviewTracking.vue'
import SkippingCustomValidationDialog from '@/components/SkippingCustomValidationDialog.vue'
import {
  BARCODE_ACTION_COMPLETE,
  GET_TRACKING,
  OPEN_DIALOG,
  RESET_ALL_BARCODE_BLOB,
  SAVE_BARCODE,
} from '@/store/actions'
import { compileMessage, reviewCalculation, updateBarcodeTrackingData } from '@/utils/api'
import {
  getLocalStorageKey,
  LEFT_AMOUNT_START_TROLLEY_INDEX,
  LOCAL_STORAGE_ITEM,
  MASKED_ANNOTATION,
  SCANNED_DEVICE_KEY,
} from '@/utils/constants'
import { Linkify } from '@/utils/directives'
import { ECustomButtonFormBuilderType, ECustomFieldType, EOperatorType } from '@/utils/enums'
import errorHandler, { processError } from '@/utils/errorHandler'
import { isDataNotEmpty, isHideVisibilityConfig, isJson, trollyNumberLimit } from '@/utils/helpers'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'
import mitt from '@/utils/mitt'
import { unsubmittedSerialCodes } from '@/utils/serialChanges'
import { IListStringItem, ITrackingRequestBody, TError, TFieldNotiSettings } from '@/utils/types/commonTypes'
import { ICustomFieldData } from '@/utils/types/fieldData'
import { IBarcodeDefinitionType, IFieldSettings } from '@/utils/types/fieldDefinition'
import { IActivationCustomField } from '@/utils/types/projectTypes'
import { cloneDeep } from 'lodash'
import isEmpty from 'lodash/isEmpty'
import { mixins, Options } from 'vue-class-component'
import { ProvideReactive, Watch } from 'vue-property-decorator'
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'

@Options({
  directives: {
    linkify: Linkify,
  },
  components: {
    FormBuilder,
    KeyenceHTHardKeyButton,
    PreviewTracking,
    ReviewCalculationDialog,
    SkippingCustomValidationDialog,
  },
  emits: ['submit', 'back'],
  name: 'TrackView',
})
export default class TrackView extends mixins(CustomFieldsMixin, BarcodeTypesMixin) {
  @ProvideReactive() isShowArrow = false
  imagesFromOCR: Record<string, string> = {}
  temporaryFieldsOCRImages: Record<string, string[]> = {}
  loading = false
  showDialogAlert = false
  showSkipValidationDialog = false
  showDuplicateCodesDialog = false
  showExistingDataErrorDialog = false
  skipValidationMessage = ''
  duplicateCodesMessage = ''
  isAllowToLeave = false
  selectedTarget = ''
  targetChoices = [] as Array<{
    key: string
    name: string
    order?: number
  }>

  dialogMessage = ''
  isShowCalculationDialog = false
  calculationContent = ''

  formBuilderKeyCounter = 0
  buttonsArrayInTrack: Array<IFormButton> = [
    {
      id: 'skipButton',
      nativeType: 'button',
      label: 'SKIP',
      type: 'danger',
      inputType: ECustomButtonFormBuilderType.BUTTON,
      onClick: () => {
        this.confirmSkipTrackingDialog()
      },
    },
  ]

  get MASKED_ANNOTATION() {
    return MASKED_ANNOTATION
  }

  get currentBarcodeTypeInfo(): IBarcodeDefinitionType {
    return this.$store.state.project.details.barcodeTypes[this.barcode.barcodeType]
  }

  get isFABButtonAvailable() {
    return this.currentBarcodeTypeInfo?.isFloatingActionButton || false
  }

  get barcode() {
    return this.$store.state.barcode.barcode
  }

  get currentTrackPointKey() {
    return this.barcode?.currentTrackPointKey
  }

  get project() {
    return this.$store.state.project.details
  }

  get forms() {
    return this.project?.trackPoints[this.currentTrackPointKey]?.trackPointForms
  }

  get currentTrackPointName() {
    return this.project?.trackPoints[this.currentTrackPointKey]?.name
  }

  // get isTargetPreSelected() {
  //   return !!this.$route.params.selectedTarget
  // }

  get currentTrackingName() {
    const trackPoint = this.targetChoices.find((val) => val.key === this.selectedTarget)
    return trackPoint?.name
  }

  get customForm(): Record<string, IFieldSettings | TFieldNotiSettings> {
    const trackingForm = this.forms && cloneDeep(this.forms[this.selectedTarget])
    if (!trackingForm?.customFields) {
      trackingForm.customFields = {} as Record<string, IActivationCustomField>
    }
    const orderList = Object.values(trackingForm?.customFields) as IActivationCustomField[]
    let maxOrder = orderList
      .map((item) => item.order)
      .reduce((accumulator, currentValue) => {
        return Math.max(accumulator, currentValue)
      }, -Infinity)

    if (this.currentBarcodeTypeInfo?.activationFields?.customFields) {
      Object.entries(this.currentBarcodeTypeInfo?.activationFields?.customFields).forEach(([key, value]) => {
        if (value.fieldType === ECustomFieldType.ADJUSTMENT) {
          if (value?.trackPointRoutes) {
            const { trackPointRoutes, minValue, maxValue, ...rest } = value
            for (const item of trackPointRoutes) {
              if (item.from === this.currentTrackPointKey && item.to === this.selectedTarget) {
                maxOrder = maxOrder + 1
                rest.order = maxOrder
                if (item?.customLabel) {
                  rest.label = item.customLabel
                }
                trackingForm.customFields[key] = rest as IActivationCustomField
              }
            }
          }
        }
      })
    }

    return trackingForm
  }

  get isAuth(): boolean {
    return this.$store.getters.isAuth
  }

  get previewAddTrackingVisiblity(): boolean {
    const previewVisibility = this.currentBarcodeTypeInfo?.uiConfig?.previewAddTrackingVisiblity || ''
    return !isHideVisibilityConfig(previewVisibility, this.isAuth, false)
  }

  previewTrackingData = {}
  async loadPreviewTrackingData() {
    const calculationFields = await Object.entries(this.selectedFormSchema)
      .filter((entry) => (entry[1] as IFieldSettings).fieldType === ECustomFieldType.CALCULATION)
      .reduce(
        async (acc, entry) => ({
          ...acc,
          [entry[0]]: {
            fieldType: ECustomFieldType.CALCULATION,
            calculation: await this.getReviewCalculation(entry[0]),
          },
        }),
        {}
      )

    this.previewTrackingData = {
      ...this.generateFormRequest.trackingData,
      customFields: {
        ...this.generateFormRequest.trackingData.customFields,
        ...calculationFields,
      },
    }
  }

  async getReviewCalculation(calculationFieldKey: string) {
    this.appendOCRImage()
    const reviewPayload = {
      field: 'trackingData',
      customFieldKey: calculationFieldKey,
      startTrackPointKey: this.currentTrackPointKey,
      endTrackPointKey: this.selectedTarget,
      barcode: {
        ...this.barcode,
        trackingData: [this.generateFormRequest.trackingData],
      },
    }

    let content = ''
    try {
      content = (await reviewCalculation(reviewPayload)).data
    } catch (e) {
      errorHandler(e as TError)
    }
    return content
  }

  async reviewCalculation(calculationFieldKey: string) {
    this.loading = true
    this.calculationContent = await this.getReviewCalculation(calculationFieldKey)
    this.isShowCalculationDialog = true
    this.loading = false
  }

  get allowSkipTracking() {
    return this.forms[this.selectedTarget].allowSkipTracking
  }

  get formBuilderKey(): string {
    // Create a reactive key that changes when formModel or selectedTarget changes
    return `${this.selectedTarget}_${this.formBuilderKeyCounter}`
  }

  get hasRemarks(): boolean {
    return !isEmpty(this.customHtml)
  }

  get customHtml(): string {
    return (
      this.barcodeType?.trackPointRouteRemarks?.find(
        (remarks) => remarks.from === this.currentTrackPointKey && remarks.to === this.selectedTarget
      )?.remarks ?? ''
    )
  }

  mergedHtml = ''
  @Watch('customHtml')
  onUpdateCustomHtml() {
    if (!this.hasRemarks) return

    const cleanedHTML = this.customHtml.replace(/(\r\n|\n|\r)/gm, '')
    if (!isEmpty(cleanedHTML)) {
      compileMessage(this.$route.params.barcodeId, this.customHtml)
        .then((res) => (this.mergedHtml = res))
        .catch((err) => errorHandler(err))
    }
  }

  get barcodeType(): IBarcodeDefinitionType {
    return this.project?.barcodeTypes?.[this.barcode?.barcodeType]
  }

  get barcodeName(): string {
    return this.barcodeType.name || ''
  }

  get isAddTrackingDialog() {
    return this.dialogMode === 'add-tracking'
  }

  dialogMode = 'add-tracking'
  async confirmSubmitDialog() {
    if (!this.showDialogAlert) {
      this.dialogMode = 'add-tracking'
      this.dialogMessage =
        this.selectedTarget === '4' ? 'confirm_complete_manufacturing_order' : 'confirm_add_track_data'
      await this.loadPreviewTrackingData()
      this.showDialogAlert = true
    }
  }

  confirmSkipTrackingDialog() {
    if (!this.showDialogAlert) {
      this.dialogMode = 'skip-add-tracking'
      this.dialogMessage = 'confirm_skip_track_data'
      this.showDialogAlert = true
    }
  }

  onBackRouter() {
    this.$router.back()
  }

  // get isAllowBulkUpdateAtCurrentTrackPoint() {
  //   return this.currentBarcodeTypeInfo?.bulkUpdatableTrackPoints.includes(this.currentTrackPointKey)
  // }

  onRouteTo(view: string, id?: string) {
    this.showDialogAlert = false
    const nextSelectedTrackingPoint: string =
      (this.targetChoices.find((val) => val.key === this.selectedTarget)?.name as string) || ''

    localStorage.tempRequestForMultipleBarcodeScan = JSON.stringify(this.generateFormRequest)

    this.$router.replace({
      name: view,
      params: {
        project: this.$store.getters.projectParam,
        barcodeId: id || this.barcode?.id,
        nextTrackName: nextSelectedTrackingPoint || '',
      },
    })
  }

  async submit(isSkip: boolean) {
    try {
      this.showDialogAlert = false
      this.loading = true
      const request = {
        ...this.generateFormRequest,
        selectedNextTrackpoint: this.nextSelectTrackingPointName,
        isSkip,
        isDryRun: false,
      }

      await updateBarcodeTrackingData(request)
      // if (this.$store.getters.trackingData) {
      //   await editTracking(request)
      // } else {
      //   await tracking(request)
      // }
      this.imagesFromOCR = {}
      this.temporaryFieldsOCRImages = {}

      this.$store.commit(RESET_ALL_BARCODE_BLOB)
      this.$store.dispatch(BARCODE_ACTION_COMPLETE, 'tracked')
      this.cleanTrackingDataDraft()
      this.isAllowToLeave = true
      this.$router.push({
        name: 'detail',
        params: { barcodeId: this.barcode.id },
      })
    } catch (error) {
      const modelObject = {
        barcodeType: this.barcodeName,
        fTP1: this.currentTrackPointName,
        tTP2: this.targetChoices.find((val) => val.key === this.selectedTarget)?.name || '',
      }
      const isSkipCustomValidator = this.isSkipCustomValidator(error as TError)
      if (isSkipCustomValidator) {
        this.handleSkippedCustomValidation(error as TError, modelObject)
        return
      }

      if (error === '3065') {
        this.showExistingDataErrorDialog = true
        return
      }

      errorHandler(error as TError, modelObject)
    } finally {
      this.loading = false
    }
  }

  cleanTrackingDataDraft() {
    Object.keys(localStorage).forEach((key) => {
      if (key === this.keyTrackingDataDraft || key === this.keyPreviousTrackingDataDraft) {
        localStorage.removeItem(key)
      }
    })
  }

  isSkipCustomValidator(error: TError) {
    if (typeof error === 'string') {
      if (isJson(error)) {
        const jsonObject = JSON.parse(error)
        return jsonObject.IsSkipCustomValidator ?? false
      }
    }
    return false
  }

  handleSkippedCustomValidation(error: TError, fieldTemplate?: Record<string, string>) {
    const { errorDetails } = processError(error, fieldTemplate)
    this.skipValidationMessage = errorDetails
    this.showSkipValidationDialog = true
  }

  async proceedWithSkippedValidation() {
    try {
      this.showSkipValidationDialog = false
      this.loading = true
      const request = {
        ...this.generateFormRequest,
        selectedNextTrackpoint: this.nextSelectTrackingPointName,
        isSkip: false,
        isDryRun: false,
        IsSkipCustomValidator: true,
      }

      await updateBarcodeTrackingData(request)

      this.imagesFromOCR = {}
      this.temporaryFieldsOCRImages = {}

      this.$store.commit(RESET_ALL_BARCODE_BLOB)
      this.$store.dispatch(BARCODE_ACTION_COMPLETE, 'tracked')
      this.cleanTrackingDataDraft()
      this.isAllowToLeave = true
      this.$router.push({
        name: 'detail',
        params: { barcodeId: this.barcode.id },
      })
    } catch (error) {
      const modelObject = {
        barcodeType: this.barcodeName,
        fTP1: this.currentTrackPointName,
        tTP2: this.targetChoices.find((val) => val.key === this.selectedTarget)?.name || '',
      }
      errorHandler(error as TError, modelObject)
    } finally {
      this.loading = false
    }
  }

  confirmMergeCurrentSerialWithNewestData() {
    this.showExistingDataErrorDialog = false
    this.handleMergeCurrentSerialWithNewestData()
  }

  scannedWorkCenterCode() {
    return localStorage.getItem(LOCAL_STORAGE_ITEM.WORK_CENTER_CODE) || ''
  }

  async loadSavedTrackingData() {
    this.loading = true
    try {
      const isContainsPreviousData = await this.handleCheckPreviousTrackingData()
      if (isContainsPreviousData) {
        this.handleGetPreviousTrackingData()
        this.isPreventSaveLocal = false
        return
      }

      this.handleGetDataFromLocalStorage()
      this.isPreventSaveLocal = false
      this.formBuilderKeyCounter++
    } finally {
      this.loading = false
    }
  }

  async handleMergeCurrentSerialWithNewestData() {
    this.loading = true
    try {
      const isContainsPreviousData = await this.handleCheckPreviousTrackingData()
      if (isContainsPreviousData) {
        this.handleGetPreviousTrackingData()
        return
      }
    } finally {
      this.loading = false
    }
  }

  get generateFormRequest() {
    this.appendOCRImage()
    const generatedCustomDataForRequest = this.generateCustomDataForRequest
    const { location, ...other } = generatedCustomDataForRequest
    const requestBody: ITrackingRequestBody = {
      barcodeIds: [this.barcode.id],
      trackingData: {
        currentTrackPointKey: this.handledTrackingPoint,
        nextTrackPointKey: this.selectedTarget,
        location,
        customFields: {
          ...other,
        },
      },
    }

    return requestBody
  }

  get handledTrackingPoint() {
    if (this.$store.getters.trackingData) {
      return this.$store.getters.trackingData.currentTrackPointKey
    }

    return this.barcode.currentTrackPointKey
  }

  isPreventSaveLocal = false

  @Watch('formModel', { deep: true })
  onUpdateFormModel() {
    if (!this.isPreventSaveLocal && this.project && this.barcode && this.currentTrackPointKey) {
      localStorage.setItem(this.keyTrackingDataDraft, JSON.stringify(this.formModel))
    }
  }

  get keyTrackingDataDraft(): string {
    return getLocalStorageKey.trackingDataDraft(this.project.code, this.barcode.id, this.selectedTarget)
  }

  get keyPreviousTrackingDataDraft(): string {
    return getLocalStorageKey.trackingDataDraft(this.project.code, this.barcode.id, this.selectedTarget + '.previous')
  }

  appendOCRImage() {
    if (this.imagesFromOCR) {
      Object.keys(this.imagesFromOCR).forEach((key) => {
        this.formModel[key] = [...this.formModel?.[key], ...this.imagesFromOCR?.[key]]
      })
    }
  }

  async handleCheckPreviousTrackingData() {
    try {
      const trackingData = await this.$store.dispatch(GET_TRACKING, {
        barcodeId: this.barcode.id,
        nextTrackPointKey: this.selectedTarget,
      })

      if (trackingData && trackingData.customFields) {
        return true
      }
      return false
    } catch (error) {
      return false
    }
  }

  formatPreviousTrackingData(rawData: Record<string, unknown>): Record<string, unknown> {
    const formattedData: Record<string, unknown> = {}
    Object.entries(rawData).forEach(([key, value]) => {
      const fieldData = value as ICustomFieldData

      switch (fieldData.fieldType) {
        case ECustomFieldType.LIST_STRING: {
          const listString = fieldData.listString ? [...fieldData.listString] : []
          formattedData[key] = listString.map((item) => this.loadItem(item))
          break
        }
        case ECustomFieldType.TEXT:
        case ECustomFieldType.EMAIL:
        case ECustomFieldType.NUMBER: {
          if (key === SCANNED_DEVICE_KEY) {
            formattedData[key] = this.scannedWorkCenterCode()
          } else {
            formattedData[key] = fieldData[fieldData.fieldType] || ''
          }
          break
        }
        default:
          formattedData[key] = value
      }
    })

    return formattedData
  }

  loadItem(item: IListStringItem): IListStringItem {
    return {
      id: item.id,
      conversionUnit: item.conversionUnit,
      itemCode: item.itemCode ?? '',
      itemName: item.itemName,
      productId: item.productId,
      code: item.code || '',
      limitAmount: item.limitAmount,
      amount: item.currentAmount,
      currentAmount: item.currentAmount,
      unit: item.unit,
      scannedDateTime: item.scannedDateTime ? new Date(item.scannedDateTime).toISOString() : new Date().toISOString(),
      initAmount: item.initAmount,
      warnings: item.warnings ?? [],
      trollyNumber: item.trollyNumber,
      trollyIndex: item.trollyIndex,
    }
  }

  lastTrollyIndex(operatorType: EOperatorType): number {
    switch (operatorType) {
      case EOperatorType.ADD:
        return this.barcode?.serialSetting.producedLastTrollyIndex
      case EOperatorType.USE:
        return this.barcode?.serialSetting.usedLastTrollyIndex
      default:
        return this.barcode?.serialSetting.correctValueLastTrollyIndex
    }
  }

  lastTrollyNumber(operatorType: EOperatorType): number {
    switch (operatorType) {
      case EOperatorType.ADD:
        return this.barcode?.serialSetting.producedLastTrollyNumber ?? 0
      case EOperatorType.USE:
        return this.barcode?.serialSetting.usedLastTrollyNumber ?? 0
      default:
        return 0
    }
  }

  mergeLocalStorageWithApiData(trackingDataFromApi: Record<string, unknown>): Record<string, unknown> {
    const trackingDataFromLocal = localStorage.getItem(this.keyTrackingDataDraft)
    if (trackingDataFromLocal) {
      try {
        const parsedLocalData = JSON.parse(trackingDataFromLocal)

        if (parsedLocalData.sirial && Array.isArray(parsedLocalData.sirial)) {
          const apiSerial = (trackingDataFromApi.sirial as IListStringItem[]) || []
          const localSerial = parsedLocalData.sirial as IListStringItem[]

          const { mergedSerial, duplicateCodes } = this.mergeSerialData(apiSerial, localSerial)
          const clonedData = cloneDeep(trackingDataFromApi)
          clonedData.sirial = mergedSerial

          if (this.shouldShowDuplicateDialog(duplicateCodes)) {
            this.duplicateCodesMessage = duplicateCodes.join(', ')
            this.showDuplicateCodesDialog = true
          }

          return clonedData
        }
      } catch (error) {
        console.error('Error parsing localStorage data:', error)
      }
    }
    return trackingDataFromApi
  }

  getUnsubmittedSerialCodes(): string[] {
    const trackingData = this.$store.getters.trackingData
    const apiSerial = (trackingData?.customFields?.sirial?.listString as IListStringItem[]) || []
    const localSerial = (this.formModel?.sirial as IListStringItem[]) || []
    const removedSerial = (this.formModel?.removed_sirial as IListStringItem[]) || []
    return unsubmittedSerialCodes(apiSerial, localSerial, removedSerial)
  }

  openUnsubmittedSerialsWarningDialog() {
    this.$store.dispatch(OPEN_DIALOG, {
      message: `<div style="font-weight: bold; text-align: center;">
        <div style="background-color: red; color: white; margin: 10px 0; padding: 5px;">
          ${this.$t('unsubmitted_serial_warning_title')}
        </div>
        <div style="color: red;">
          <div>${this.$t('unsubmitted_serial_warning_message')}</div>
        </div>
      </div>`,
      onConfirm: () => this.submit(false),
    })
  }

  mergeSerialData(apiSerial: IListStringItem[], localSerial: IListStringItem[]) {
    const apiIds = new Set(apiSerial.map((item) => item.id))
    const duplicateCodes: string[] = []

    const merged = [...apiSerial]
    for (const localItem of localSerial) {
      if (apiIds.has(localItem.id)) {
        duplicateCodes.push(localItem.code)
      } else {
        merged.push(localItem)
      }
    }
    merged.sort((a, b) => {
      const dateA = new Date(a.scannedDateTime || 0).getTime()
      const dateB = new Date(b.scannedDateTime || 0).getTime()
      return dateA - dateB
    })
    const operatorType = this.barcode.serialSetting.operatorType
    const limit = trollyNumberLimit(merged)
    merged.forEach((item, index) => {
      const newIndex =
        operatorType === EOperatorType.CORRECT_VALUE ? LEFT_AMOUNT_START_TROLLEY_INDEX + index : index + 1
      const newTrollyNumber = operatorType === EOperatorType.CORRECT_VALUE ? 0 : Math.ceil(newIndex / limit)
      item.trollyIndex = newIndex
      item.trollyNumber = newTrollyNumber
    })

    return { mergedSerial: merged, duplicateCodes }
  }

  shouldShowDuplicateDialog(duplicateCodes: string[]): boolean {
    return duplicateCodes.length > 0
  }

  handleGetPreviousTrackingData() {
    const trackingData = this.$store.getters.trackingData
    if (trackingData && trackingData.customFields && this.barcode) {
      const trackingDataFromApi = this.formatPreviousTrackingData(trackingData.customFields)

      const listStringApiSerial = this.processListStringSerial(trackingDataFromApi.sirial as IListStringItem[])
      const listStringPrevious = localStorage.getItem(this.keyPreviousTrackingDataDraft)
      if (listStringPrevious && listStringApiSerial.join(', ') === listStringPrevious) {
        const trackingDataFromLocal = localStorage.getItem(this.keyTrackingDataDraft)
        if (trackingDataFromLocal) {
          const parsedLocalData = JSON.parse(trackingDataFromLocal)
          this.formModel = { ...parsedLocalData }
        } else {
          this.formModel = { ...trackingDataFromApi }
        }
      }
      const mergedData = this.mergeLocalStorageWithApiData(trackingDataFromApi)
      this.formModel = { ...mergedData }
      localStorage.setItem(this.keyTrackingDataDraft, JSON.stringify(this.formModel))
      this.generateCustomFormModel(undefined, true)
      this.formBuilderKeyCounter++
      if (!this.showDuplicateCodesDialog) {
        this.savePreviousTrackingData(trackingData.customFields.sirial.listString as IListStringItem[])
      }
    }
  }

  savePreviousTrackingData(listSerial: IListStringItem[]) {
    const codes = this.processListStringSerial(listSerial)
    localStorage.setItem(this.keyPreviousTrackingDataDraft, codes.join(', '))
  }

  processListStringSerial(listSerial: IListStringItem[]): string[] {
    if (listSerial && Array.isArray(listSerial)) {
      return listSerial.map((item) => item.code)
    }
    return []
  }

  handleGetDataFromLocalStorage() {
    const valueFromLocalStorage = localStorage.getItem(this.keyTrackingDataDraft)
    if (!valueFromLocalStorage) {
      this.generateCustomFormModel(undefined)
      return
    }
    const parsedValue = JSON.parse(valueFromLocalStorage)
    if (valueFromLocalStorage && isDataNotEmpty(parsedValue)) {
      this.isPreventSaveLocal = true

      if (parsedValue && parsedValue.machine !== this.scannedWorkCenterCode()) {
        parsedValue.machine = this.scannedWorkCenterCode()
        localStorage.setItem(this.keyTrackingDataDraft, JSON.stringify(parsedValue))
      }
      this.formModel = parsedValue
      this.generateCustomFormModel(undefined, true)
    }
  }

  isDisabledEditing(key: string): boolean {
    // this function is used to make sure only the selected node is edited, prevent chosing other nodes
    let result = false
    if (this.$route.params.keyEdit) {
      if (key === this.$route.params.keyEdit) {
        result = false
      } else {
        result = true
      }
    }
    return result
  }

  async mounted() {
    const project = this.$store.state.project.details
    if (this.forms) {
      const keyEdit = this.$route.params.keyEdit
      if (keyEdit) {
        if (this.barcode) {
          const clonedBarcode = { ...this.barcode }
          clonedBarcode.currentTrackPointKey = this.$route.params.prevTrackPointKey
          this.$store.dispatch(SAVE_BARCODE, { ...clonedBarcode })
        }
      }
      this.targetChoices = Object.keys(this.forms).map((stepKey) => ({
        key: stepKey,
        name: project.trackPoints[stepKey].name,
        order: this.forms[stepKey].order,
      }))

      if (this.targetChoices.length > 0) {
        this.targetChoices.sort((a, b) => (a.order && b.order && a.order - b.order) || 0)
        const preSelectedTarget = this.$route.params.selectedTarget
        this.selectedTarget = keyEdit || preSelectedTarget || this.targetChoices[0].key
      }
      if (keyEdit) {
        if (this.barcode) {
          this.formModel = this.convertProcessedDataToOriginal(
            this.barcode.trackingData[this.$route.params.index].customFields
          )
        } else {
          this.generateCustomFormModel(undefined, true)
        }
      } else {
        this.isPreventSaveLocal = true
        this.formModel = {}
        await this.loadSavedTrackingData()
      }
    }

    mitt.on('update:trackInfo', (event) => {
      this.formModel = Object.assign(this.formModel, event)
    })

    mitt.on('update:fileList', (event) => {
      this.fileListFormModel = Object.assign(this.fileListFormModel, event)
    })

    mitt.on('update:imageOCR', (event) => this.saveOCRImage(event))
  }

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  get KeyenceHardKey() {
    return KeyenceHardKey
  }

  closeDuplicateCodesDialog() {
    this.showDuplicateCodesDialog = false
  }

  beforeRouteLeave(_to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) {
    if (this.isAllowToLeave) {
      next()
      return
    }
    const unsubmittedSerialCodes = this.getUnsubmittedSerialCodes()
    if (unsubmittedSerialCodes.length > 0) {
      this.openUnsubmittedSerialsWarningDialog()
      next(false)
      return
    }
    next()
  }

  async syncTrackingDataFromApi() {
    try {
      await this.$confirm(this.$t('confirm_sync_data'), this.$t('confirm'), {
        confirmButtonText: this.$t('ok'),
        cancelButtonText: this.$t('cancel'),
      })
      this.closeDuplicateCodesDialog()
      localStorage.removeItem(this.keyTrackingDataDraft)
      localStorage.removeItem(this.keyPreviousTrackingDataDraft)
      await this.loadSavedTrackingData()
    } catch {}
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/css/mixins.scss';

.current-tracking-point-container {
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom: 2px solid $border-color;
  padding: 5px;
  background: #dadada;
  border-radius: 12px;
  line-height: 1.5;
  text-align: center;
  font-weight: 600;
  color: #495057;
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.remarks-content {
  @include label-box;
  text-align: left;
  border-radius: 5px !important;
  width: 100%;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.section {
  margin-top: 12px;
  margin-bottom: 20px;
}

hr.line-break {
  margin: 5px 0 15px 0;
}

.section-header {
  line-height: 2;
  text-align: left;

  &.shrink {
    margin-bottom: 0;
  }
}

.info {
  color: $main;
}

.next-trackingPoint {
  padding: 4px 0;
}

.section-trackpoints {
  .el-radio {
    margin-right: 0px;
  }
}

.key-and-value {
  display: flex;
  margin: 8px 0px;
  align-items: center;
  text-align: left;
  justify-content: flex-start;

  .label {
    margin-right: 8px;
    font-weight: 600;
    white-space: nowrap;
  }

  .value {
    width: 100%;
  }
}
</style>
